import { ScrollView, Text, View } from 'react-native'
import { useMutation, useQuery } from '@apollo/client'
import { Redirect, useLocalSearchParams, usePathname } from 'expo-router'
import { useTranslation } from 'react-i18next'

import { APPROVE_CONNECT_APP_SESSION } from '@/constants/graphql/mutations'
import { GET_CONNECT_APP_SESSION } from '@/constants/graphql/queries'
import { marketPresentation } from '@/config/marketPresentation'
import { convertToDate } from '@/utils/formatter'
import { useRouter } from '@/utils/navigation'
import { AuthContext } from '@/stores/auth'
import { Button, useToast } from '@/components/base'
import BottomActionPanel from '@/components/BottomActionPanel'
import DescriptionListItem from '@/components/DescriptionListItem'
import { Stack } from '@/components/layout'
import LoadingScreen from '@/components/LoadingScreen'
import NavigationBar from '@/components/NavigationBar'
import NotFoundScreen from '@/components/NotFoundScreen'

type ConnectAppSession = {
  connectId: string
  order: string
  partnerName: string
  state: number
  expiresAt: string
}

export default function ConnectAppLandingScreen() {
  const pathname = usePathname()
  const auth = AuthContext.useActorRef()
  const params = useLocalSearchParams<Record<string, string>>()
  const { connectId } = params

  if (!auth.getSnapshot().matches('LOGGED_IN')) {
    const redirectParams = {
      redirect: encodeURIComponent(pathname),
      params: encodeURIComponent(JSON.stringify({ connectId }))
    }
    return <Redirect href={{ pathname: '/login', params: redirectParams }} />
  }

  if (!connectId) {
    return <NotFoundScreen />
  }

  return <ConnectAppScreen connectId={connectId} />
}

function ConnectAppScreen({ connectId }: { connectId: string }) {
  const { t } = useTranslation()
  const toast = useToast()
  const router = useRouter()

  const { data, loading, error } = useQuery(GET_CONNECT_APP_SESSION, {
    variables: { connectId },
    onError: (queryError) => {
      console.log(queryError)
    }
  })

  const [approveConnectAppSession, { loading: approving }] = useMutation(APPROVE_CONNECT_APP_SESSION, {
    onCompleted: () => {
      toast.show({ variant: 'success', description: t('profile:connectAppApproveSuccess') })
      router.dismissAll()
      router.replace('/home')
    },
    onError: (mutationError) => {
      console.log(mutationError)
      toast.show({ variant: 'error', description: t('profile:connectAppApproveFailed') })
    }
  })

  const session = data?.getConnectAppSession as ConnectAppSession | undefined

  if (loading) {
    return <LoadingScreen />
  }

  if (error || !session) {
    return <NotFoundScreen />
  }

  return (
    <>
      <Stack.Screen
        options={{
          header: () => <NavigationBar hasBottomBorder={true}>{t('profile:connectAppTitle')}</NavigationBar>
        }}
      />
      <View className={'flex-1 bg-background-secondary'}>
        <ScrollView>
          <View className={'mt-4 overflow-hidden border-y border-border-10 bg-background-primary'}>
            <DescriptionListItem label={t('profile:connectAppName')} hasBorderTop={true}>
              {session.partnerName}
            </DescriptionListItem>
            <DescriptionListItem label={t('profile:connectAppOrder')}>{session.order}</DescriptionListItem>
            <DescriptionListItem label={t('profile:connectAppExpiresAt')} hasBorderBottom={true}>
              {session.expiresAt ? convertToDate(new Date(session.expiresAt).valueOf()) : '-'}
            </DescriptionListItem>
          </View>
          <View className={'px-4 pt-4'}>
            <Text className={'text-sm text-text-secondary'}>
              {t('profile:connectAppConfirmDesc', { token: marketPresentation.platformToken.displayName })}
            </Text>
          </View>
        </ScrollView>
        <BottomActionPanel>
          <Button
            className={'flex-1'}
            size={'md'}
            onPress={() => approveConnectAppSession({ variables: { connectId } })}
            disabled={approving}
          >
            {t('profile:connectAppConfirm')}
          </Button>
        </BottomActionPanel>
      </View>
    </>
  )
}
